/**
 * Small Windows→IANA time-zone mapping for the values exposed in
 * `SettingsTab.tsx` (facility Time Zone dropdown). `tblFacility.
 * TimeZone_ID` stores Windows IDs (e.g. "Eastern Standard Time")
 * because the legacy WinForms app uses `TimeZoneInfo` which is
 * keyed by Windows IDs. Browsers need IANA IDs.
 *
 * Extend when Settings adds new dropdown entries.
 */
const WINDOWS_TO_IANA: Record<string, string> = {
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  'Alaskan Standard Time': 'America/Anchorage',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Mountain Standard Time': 'America/Denver',
  'Central Standard Time': 'America/Chicago',
  'Eastern Standard Time': 'America/New_York',
  'Atlantic Standard Time': 'America/Halifax',
  // "US Eastern" observes DST like America/New_York on server-2016+
  // but does NOT observe DST on older Windows entries — however
  // the facility rarely picks this. Mirror server behavior.
  'US Eastern Standard Time': 'America/Indianapolis',
  'US Mountain Standard Time': 'America/Phoenix',
};

/** Map a Windows time-zone ID (`tblFacility.TimeZone_ID`) to an
 *  IANA ID the browser's `Intl.DateTimeFormat` accepts. Returns
 *  `null` when the value is empty or unknown. */
export function windowsToIana(winId: string | null | undefined): string | null {
  if (!winId) return null;
  return WINDOWS_TO_IANA[winId] ?? null;
}

/** Minutes from UTC for `zone` at `date` (Eastern daylight = -240). */
export function zoneOffsetMinutes(date: Date, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(date);
  const n = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'));
  const whole = date.getTime() - date.getUTCMilliseconds();
  return Math.round((asUtc - whole) / 60000);
}

/** Whether `zone` changes its offset during the year (has daylight saving time). */
export function zoneHasDst(zone: string, year = new Date().getUTCFullYear()): boolean {
  return zoneOffsetMinutes(new Date(Date.UTC(year, 0, 15)), zone)
    !== zoneOffsetMinutes(new Date(Date.UTC(year, 6, 15)), zone);
}

/** Standard-time offset of `zone` (the smaller of its January and July offsets). */
function standardOffsetMinutes(zone: string, year: number): number {
  return Math.min(
    zoneOffsetMinutes(new Date(Date.UTC(year, 0, 15)), zone),
    zoneOffsetMinutes(new Date(Date.UTC(year, 6, 15)), zone),
  );
}

/**
 * The zone to display a facility's times in: `tblFacility.TimeZone_ID` plus
 * `TimeZone_UseDST`, the way WinForms reads them
 * (`FacilitySettings.GetLocalFacilityTime`). With UseDST off WinForms takes an
 * hour off while the zone is on daylight time, so the facility stays on
 * standard time all year; that is a fixed-offset `Etc/GMT` zone here. Returns
 * `null` when the zone is empty or unknown.
 */
export function facilityTimeZone(
  winId: string | null | undefined,
  useDst: boolean | null | undefined,
  year = new Date().getUTCFullYear(),
): string | null {
  const iana = windowsToIana(winId);
  if (!iana || useDst !== false || !zoneHasDst(iana, year)) return iana;
  const std = standardOffsetMinutes(iana, year);
  // Etc/GMT signs are inverted (Etc/GMT+6 is UTC-6) and only whole hours exist;
  // every zone in the map is whole-hour.
  if (std % 60 !== 0) return iana;
  const h = -std / 60;
  return h === 0 ? 'Etc/GMT' : `Etc/GMT${h > 0 ? '+' : '-'}${Math.abs(h)}`;
}

/** "UTC", "UTC-5", "UTC+5:30". */
export function formatUtcOffset(minutes: number): string {
  if (minutes === 0) return 'UTC';
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/** "Central Standard Time" -> "Central". Unknown or blank ids come back as given. */
export function shortZoneName(winId: string | null | undefined): string {
  if (!winId) return '';
  return winId.trim().replace(/ Standard Time$/i, '');
}

/** The browser's long name for `zone` at `date`, e.g. "Central Daylight Time". */
export function longZoneName(date: Date, zone: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'long' })
      .formatToParts(date).find((p) => p.type === 'timeZoneName');
    return part?.value ?? zone;
  } catch {
    return zone;
  }
}

// Reverse index (IANA → Windows). Covers every IANA value that
// `WINDOWS_TO_IANA` targets, plus a handful of common aliases the
// browser returns even though they map to the same Windows zone.
const IANA_TO_WINDOWS: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const [win, iana] of Object.entries(WINDOWS_TO_IANA)) out[iana] = win;
  // Common IANA aliases the browser may report that resolve to the
  // same Windows label as an entry above. Add more as they surface.
  out['America/Detroit'] = 'Eastern Standard Time';
  out['America/Indiana/Indianapolis'] = 'US Eastern Standard Time';
  out['America/Kentucky/Louisville'] = 'Eastern Standard Time';
  out['America/Boise'] = 'Mountain Standard Time';
  out['America/Phoenix'] = 'US Mountain Standard Time';
  return out;
})();

/** Map an IANA time-zone ID (e.g. what the browser reports via
 *  `Intl.DateTimeFormat().resolvedOptions().timeZone`) back to a
 *  Windows ID suitable for user-facing labels. Returns `null`
 *  when the IANA value isn't in the small mapping table above —
 *  the caller decides whether to fall back to the raw IANA name. */
export function ianaToWindows(ianaId: string | null | undefined): string | null {
  if (!ianaId) return null;
  return IANA_TO_WINDOWS[ianaId] ?? null;
}

/** Convert whatever the backend sent for a UTC-anchored datetime
 *  (SQL `datetime` serialized without a Z suffix) into a JS Date
 *  that actually represents that UTC instant. Without the Z the
 *  browser assumes local time and displays wrong values. */
export function parseUtcDate(input: string | Date | null | undefined): Date | null {
  if (input == null) return null;
  if (input instanceof Date) return input;
  const s = String(input);
  if (!s) return null;
  // Already has a zone marker — parse as-is.
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  // No zone — treat as UTC by forcing a Z suffix.
  return new Date(`${s}Z`);
}

/** Browser's IANA time zone (e.g. "America/New_York"). */
export function browserTimeZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

interface FormatOptions {
  /** Include milliseconds after the seconds (e.g. "3:14:07.123 PM"). */
  showMillis?: boolean;
}

/** Format a UTC-anchored Date in the requested IANA zone (or UTC).
 *  Output shape: "M/D/YYYY h:mm:ss[.mmm] AM/PM".
 *  `zone` — pass `'UTC'` for UTC, or any IANA ID for a local zone. */
export function formatDateTimeInZone(
  date: Date | null,
  zone: string,
  opts: FormatOptions = {},
): string {
  if (!date) return '-';
  const dateFmt = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'numeric', day: 'numeric', timeZone: zone,
  });
  const timeFmt = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    timeZone: zone,
  });
  let timePart = timeFmt.format(date);
  if (opts.showMillis) {
    // Milliseconds don't shift by zone — always the same 0-999.
    const ms = String(date.getUTCMilliseconds()).padStart(3, '0');
    const m = timePart.match(/^(\d{1,2}:\d{2}:\d{2})(\s*[AP]M)?$/i);
    timePart = m ? `${m[1]}.${ms}${m[2] ?? ''}` : `${timePart}.${ms}`;
  }
  return `${dateFmt.format(date)} ${timePart}`;
}
